import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deleteUser,
  disableUser,
  enableUser,
} from '../../lib/identityRegistry';
import { SCOPE_WILDCARD, USER_STATUS } from '../../lib/constants';
import type { FacilityRecord, RoleRecord, UserRecord } from '../../types/registry';
import { UserEditDrawer } from './UserEditDrawer';
import { ConfirmDialog } from '../ConfirmDialog';

interface UserTableProps {
  users: UserRecord[];
  roles: RoleRecord[];
  facilities: FacilityRecord[];
}

function statusClass(status: string): string {
  if (status === USER_STATUS.ACTIVE) return 'status-pill status-pill--active';
  if (status === USER_STATUS.DISABLED) return 'status-pill status-pill--disabled';
  return 'status-pill status-pill--invited';
}

function summarizeScope(user: UserRecord, facilities: FacilityRecord[]): string {
  if (!user.assigned_instances?.length) return 'No facilities';
  return user.assigned_instances
    .map((instance) => {
      const facility = facilities.find((entry) => entry.facility_id === instance.facility_id);
      const facilityName = facility?.name ?? instance.facility_id;
      const areas = instance.area_ids?.includes(SCOPE_WILDCARD)
        ? 'all areas'
        : `${instance.area_ids?.length ?? 0} areas`;
      return `${facilityName} (${areas})`;
    })
    .join(', ');
}

function roleDisplayName(user: UserRecord, roles: RoleRecord[]): string {
  const match = roles.find((entry) => entry.role_id === user.role);
  if (match?.role_name) return match.role_name;

  const prefix = `role_${user.client_id}_`;
  if (user.role.startsWith(prefix)) return user.role.slice(prefix.length);

  return user.role.split('_').pop() ?? user.role;
}

export function UserTable({ users, roles, facilities }: UserTableProps): JSX.Element {
  const queryClient = useQueryClient();
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [pendingDelete, setPendingDelete] = useState<UserRecord | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const disableMutation = useMutation({
    mutationFn: disableUser,
    onSuccess: invalidate,
    onError: (error) => setActionError(error instanceof Error ? error.message : 'Action failed'),
  });

  const enableMutation = useMutation({
    mutationFn: enableUser,
    onSuccess: invalidate,
    onError: (error) => setActionError(error instanceof Error ? error.message : 'Action failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: async () => {
      await invalidate();
      setPendingDelete(null);
    },
    onError: (error) => {
      setActionError(error instanceof Error ? error.message : 'Action failed');
      setPendingDelete(null);
    },
  });

  if (!users.length) {
    return <div className="empty-state">No users found for this client.</div>;
  }

  return (
    <>
      {actionError ? <div className="form-error">{actionError}</div> : null}
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Scope</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.user_id}>
              <td>{user.alias?.trim() ? user.alias : '—'}</td>
              <td>{roleDisplayName(user, roles)}</td>
              <td>
                <span className={statusClass(user.status)}>{user.status}</span>
              </td>
              <td>{summarizeScope(user, facilities)}</td>
              <td>
                <div className="table-actions">
                  <button type="button" onClick={() => setEditingUser(user)}>
                    Edit
                  </button>
                  {user.status === USER_STATUS.DISABLED ? (
                    <button
                      type="button"
                      disabled={enableMutation.isPending}
                      onClick={() => enableMutation.mutate(user.user_id)}
                    >
                      Enable
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={disableMutation.isPending}
                      onClick={() => disableMutation.mutate(user.user_id)}
                    >
                      Disable
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={deleteMutation.isPending}
                    onClick={() => {
                      setActionError(null);
                      setPendingDelete(user);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editingUser ? (
        <UserEditDrawer
          user={editingUser}
          roles={roles}
          facilities={facilities}
          onClose={() => setEditingUser(null)}
        />
      ) : null}

      {pendingDelete ? (
        <ConfirmDialog
          title="Delete user"
          message={`Delete "${pendingDelete.email ?? pendingDelete.cognito_username ?? pendingDelete.user_id}"? This cannot be undone.`}
          busy={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(pendingDelete.user_id)}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}
    </>
  );
}
